import { request } from "$/lib/api"
import {
  Message,
  Route,
  SaveStoreCustomer,
  StoreCustomerAlreadyExists,
  StoreCustomerDetail,
  StoreCustomerList,
  StoreCustomerListItem,
  StoreCustomerSortBy,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { isAxiosError } from "axios"

export interface StoreCustomerListParams {
  page: number
  size: number
  search?: string
  sortBy: StoreCustomerSortBy
  sortOrder: "asc" | "desc"
}

async function fetchStoreCustomers(
  storeId: string,
  { search, ...params }: StoreCustomerListParams,
): Promise<StoreCustomerList> {
  return await request<StoreCustomerList>({
    url: populateRoute(Route.STORE_CUSTOMER__FIND_MANY, storeId),
    method: "GET",
    params: search ? { ...params, search } : params,
  })
}

async function searchStoreCustomers(
  storeId: string,
  query: string,
): Promise<StoreCustomerListItem[]> {
  return await request<StoreCustomerListItem[]>({
    url: populateRoute(Route.STORE_CUSTOMER__SEARCH, storeId),
    method: "GET",
    params: { query },
  })
}

async function fetchStoreCustomer(
  storeCustomerId: string,
): Promise<StoreCustomerDetail> {
  return await request<StoreCustomerDetail>({
    url: populateRoute(Route.STORE_CUSTOMER__FIND_ONE, storeCustomerId),
    method: "GET",
  })
}

async function fetchStoreCustomerMessages(
  storeCustomerId: string,
): Promise<Message[]> {
  const messages = await request<Message[]>({
    url: populateRoute(
      Route.MESSAGE_CONTROLLER__FIND_MANY_BY_STORE_CUSTOMER,
      storeCustomerId,
    ),
    method: "GET",
  })

  // Same parsing as a job's messages: the pending bubble counts down from
  // scheduledSendAt, so it has to be a Date.
  return messages.map((message) =>
    "scheduledSendAt" in message
      ? {
          ...message,
          createdAt: new Date(message.createdAt),
          scheduledSendAt: message.scheduledSendAt
            ? new Date(message.scheduledSendAt)
            : null,
        }
      : { ...message, createdAt: new Date(message.createdAt) },
  )
}

async function createStoreCustomer(
  storeId: string,
  data: SaveStoreCustomer,
): Promise<StoreCustomerDetail> {
  return await request<StoreCustomerDetail>({
    url: populateRoute(Route.STORE_CUSTOMER__CREATE, storeId),
    method: "POST",
    data,
  })
}

async function updateStoreCustomer(
  storeCustomerId: string,
  data: SaveStoreCustomer,
): Promise<StoreCustomerDetail> {
  return await request<StoreCustomerDetail>({
    url: populateRoute(Route.STORE_CUSTOMER__UPDATE, storeCustomerId),
    method: "PATCH",
    data,
  })
}

/**
 * The id of the customer that already has this phone number, when a create
 * or update was refused because of it.
 */
export const getExistingStoreCustomerId = (error: Error): string | null => {
  if (!isAxiosError<StoreCustomerAlreadyExists>(error)) return null
  if (error.response?.status !== 409) return null
  return error.response.data.existingStoreCustomerId
}

export const useStoreCustomerMutations = (storeId: string) => {
  const queryClient = useQueryClient()

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: storeCustomerQueries.all() })

  const createMutation = useMutation({
    mutationFn: (data: SaveStoreCustomer) => createStoreCustomer(storeId, data),
    onSettled: invalidate,
  })

  const updateMutation = useMutation({
    mutationFn: ({
      storeCustomerId,
      data,
    }: {
      storeCustomerId: string
      data: SaveStoreCustomer
    }) => updateStoreCustomer(storeCustomerId, data),
    onSettled: invalidate,
  })

  return { createMutation, updateMutation }
}

const storeCustomerQueries = {
  all: () => ["store-customers"],
  lists: () => [...storeCustomerQueries.all(), "list"],
  list: (storeId: string, params: StoreCustomerListParams) =>
    queryOptions({
      queryKey: [...storeCustomerQueries.lists(), storeId, params],
      queryFn: () => fetchStoreCustomers(storeId, params),
    }),
  search: (storeId: string, query: string) =>
    queryOptions({
      queryKey: [...storeCustomerQueries.all(), "search", storeId, query],
      queryFn: () => searchStoreCustomers(storeId, query),
    }),
  details: () => [...storeCustomerQueries.all(), "detail"],
  detail: (storeCustomerId: string) =>
    queryOptions({
      queryKey: [...storeCustomerQueries.details(), storeCustomerId],
      queryFn: () => fetchStoreCustomer(storeCustomerId),
    }),
  messages: (storeCustomerId: string) =>
    queryOptions({
      queryKey: [...storeCustomerQueries.all(), "messages", storeCustomerId],
      queryFn: () => fetchStoreCustomerMessages(storeCustomerId),
    }),
}

export default storeCustomerQueries
